import { z } from 'zod';

/**
 * Zod schemas for note-related entities
 * These provide runtime validation and type inference
 */

// Base note schema - matches BaseNote interface
export const baseNoteSchema = z.object({
  id: z.string(),
  content: z.string().min(1, 'Note content is required'),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  category: z.string().optional(),
  isImportant: z.boolean().optional(),
  attachments: z.array(z.string()).optional(),
});

// Property note schema
export const propertyNoteSchema = baseNoteSchema.extend({
  propertyId: z.string(),
});

// Tenant note schema
export const tenantNoteSchema = baseNoteSchema.extend({
  tenantId: z.string(),
});

// Owner note schema
export const ownerNoteSchema = baseNoteSchema.extend({
  ownerId: z.string(),
});

// Vendor note schema
export const vendorNoteSchema = baseNoteSchema.extend({
  vendorId: z.string(),
});

// Work order note schema
export const workOrderNoteSchema = baseNoteSchema.extend({
  workOrderId: z.string(),
});

// Application note schema
export const applicationNoteSchema = baseNoteSchema.extend({
  applicationId: z.string(),
});

// Aggregated note schema (for useAllNotes) - matches NoteEntityType in types/common/notes.ts
export const aggregatedNoteSchema = baseNoteSchema.extend({
  entityType: z.enum(['property', 'owner', 'tenant', 'vendor', 'workOrder', 'application', 'unit', 'lease'] as const),
  entityId: z.string(),
  entityName: z.string(),
});

// Generic note schema that accepts any note type
export const noteSchema = baseNoteSchema.passthrough(); // Allow extra fields for entity-specific notes

/** Polymorphic note rows returned by GET `/.../notes` (Prisma + noteRepository map). */
const noteEntityTypeEnum = z.enum([
  'property',
  'owner',
  'tenant',
  'vendor',
  'workOrder',
  'application',
  'unit',
  'lease',
]);

export const entityNoteListItemSchema = z
  .object({
    id: z.string(),
    content: z.string(),
    entityType: noteEntityTypeEnum,
    entityId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    isImportant: z.boolean().optional(),
    operatingOrganizationId: z.string().optional(),
    isSystemGenerated: z.boolean().optional(),
  })
  .passthrough();

export type BaseNote = z.infer<typeof baseNoteSchema>;
export type AggregatedNote = z.infer<typeof aggregatedNoteSchema>;
